import React, { useRef, useState, useCallback } from 'react';

interface ImageUploadProps {
  value: string[];
  onChange: (urls: string[]) => void;
}

function compressImage(file: File, maxWidth = 800, quality = 0.65): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(); };
    img.src = objectUrl;
  });
}

const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange }) => {
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(async (files: FileList) => {
    setProcessing(true);
    const results: string[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      try {
        results.push(await compressImage(file));
      } catch {}
    }
    onChange([...value, ...results]);
    setProcessing(false);
  }, [value, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleMoveLeft = (index: number) => {
    if (index === 0) return;
    const next = [...value];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !processing && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? 'var(--white)' : 'var(--border)'}`,
          padding: '1.75rem 1rem',
          textAlign: 'center',
          cursor: processing ? 'default' : 'pointer',
          background: dragging ? 'rgba(255,255,255,0.04)' : 'var(--light-grey)',
          transition: 'border-color 0.2s, background 0.2s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => e.target.files && processFiles(e.target.files)}
          onClick={(e) => (e.currentTarget.value = '')}
        />
        {processing ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: 0 }}>Processing images…</p>
        ) : (
          <>
            <svg style={{ marginBottom: '0.5rem', opacity: 0.35 }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 0.2rem', fontWeight: 600 }}>
              Drag & drop images here
            </p>
            <p style={{ color: 'var(--mid-grey)', fontSize: '0.7rem', margin: 0 }}>
              or click to browse · JPEG, PNG, WebP · multiple allowed
            </p>
          </>
        )}
      </div>

      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
          {value.map((src, i) => (
            <div key={i} style={{ position: 'relative', width: 88, height: 66, flexShrink: 0 }}>
              <img
                src={src}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {i > 0 && (
                <button
                  type="button"
                  title="Move left (set as first)"
                  onClick={() => handleMoveLeft(i)}
                  style={thumbBtn('left')}
                >
                  ‹
                </button>
              )}
              {i === 0 && (
                <div style={{
                  position: 'absolute', bottom: 2, left: 2,
                  background: 'rgba(0,0,0,0.7)',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.5rem', fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  padding: '1px 4px',
                }}>
                  Cover
                </div>
              )}
              <button
                type="button"
                title="Remove"
                onClick={() => handleRemove(i)}
                style={thumbBtn('right')}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function thumbBtn(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute',
    top: 2,
    [side]: 2,
    width: 18,
    height: 18,
    background: 'rgba(0,0,0,0.72)',
    border: '1px solid rgba(255,255,255,0.25)',
    color: 'white',
    fontSize: '0.75rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    padding: 0,
  };
}

export default ImageUpload;
